import { useState } from "react";
import type { FormEvent } from "react";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6c437qhq9pk5mecscg";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "风流女管家 - 联系我们",
    description:
      "联系风流女管家影视内容站：如有内容建议、合作咨询或版权相关事宜，欢迎通过联系表单或站点邮箱与我们取得联系。",
    keywords: "风流女管家,联系我们,内容建议,合作咨询",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot: silently "succeed" for bots before any request is sent.
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写完整的姓名、邮箱和留言内容。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容不得超过 500 字。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value);
      if (str.trim() === "") return;
      payload.append(key, str);
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && String(serverMsg).toLowerCase() !== "form data is spam"
            ? String(serverMsg)
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
          <p className="text-xs font-medium text-primary-600">联系我们</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            有任何想法，欢迎告诉我们
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            内容建议、合作咨询或版权相关事宜，都可以通过下方表单与我们联系，我们会尽快回复。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          {/* Form */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-mail-send-line" />
              </span>
              在线留言
            </h2>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
              noValidate
            >
              <div className="field-verify-wrap">
                <label htmlFor="contact-website-alt">
                  网站备用信息
                  <input
                    id="contact-website-alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                  姓名 <span className="text-primary-600">*</span>
                </span>
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的姓名"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                  邮箱 <span className="text-primary-600">*</span>
                </span>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="请输入你的邮箱"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                  留言内容 <span className="text-primary-600">*</span>
                </span>
                <textarea
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="请输入你想咨询或反馈的内容（不超过 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>

              {status === "error" && formError && (
                <p className="flex items-center gap-1.5 text-sm text-primary-700">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}
              {status === "success" && (
                <p className="flex items-center gap-1.5 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line" />
                  留言已发送成功，感谢你的反馈，我们会尽快回复你。
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    正在发送...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    发送留言
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact info */}
          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                联系方式
              </h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-mail-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">
                      站点邮箱
                    </span>
                    <span className="block text-foreground-900">
                      contact@fengliu-nvguandian.com
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">
                      回复时间
                    </span>
                    <span className="block text-foreground-900">
                      工作日 09:00 - 18:00
                    </span>
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                客服微信
              </h2>
              <div className="mx-auto mt-4 h-40 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=minimal%20illustration%20of%20a%20scannable%20qr%20code%20pattern%20in%20dark%20red%20and%20white%20on%20a%20clean%20light%20background%2C%20flat%20graphic%20style%2C%20simple%20and%20clear&width=400&height=400&seq=contact-wechat-qr&orientation=squarish"
                  alt="客服微信二维码示意图"
                  title="客服微信二维码 示意图"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                扫码添加客服微信（图片为示意，仅作展示）
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}